<!-- Success/Error Messages -->
        @if (successMessage) {
<div class="alert alert-success">
          <mat-icon>check_circle</mat-icon>
          {{ successMessage }}
        </div>
}

        @if (error) {
<div class="alert alert-danger">
          <mat-icon>error_outline</mat-icon>
          {{ error }}
        </div>
}

        @if (loading) {
      <app-modernization-readiness-skeleton></app-modernization-readiness-skeleton>
      }

        <!-- Tabs -->
        @if (!loading) {
<mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedTabChange)="onTabChange($event)">
          
          <!-- Tab 1: Settings -->
          <mat-tab label="Settings">
            <div style="padding: 20px;">
              <h3>AI Prompt Configuration</h3>
              <p class="text-muted">
                Configure the AI prompts and settings for calculating modernization readiness scores. Settings are versioned for rollback capability.
              </p>

              <!-- Active Settings Display -->
              @if (activeSettings && !editingSettings) {
<div class="card" style="margin-bottom: 20px;">
                <div class="card-body">
                  <div style="display: flex; justify-content: space-between; align-items: center;">
                    <h5>
                      Active Settings (Version {{ activeSettings.version }})
                      @if (activeSettings.versionName) {
<span class="badge badge-info" style="margin-left: 10px;">
                        {{ activeSettings.versionName }}
                      </span>
}
                    </h5>
                    <div style="display: flex; gap: 10px;">
                      <button type="button" class="btn btn-sm btn-secondary no-arrow" (click)="editSettings(activeSettings)">
                        Edit
                      </button>
                      <button type="button" class="btn btn-sm btn-info no-arrow" (click)="loadVersionHistory()">
                        Version History
                      </button>
                      <button type="button" class="btn btn-sm btn-primary no-arrow" (click)="startCreateSettings()">
                        New Version
                      </button>
                    </div>
                  </div>

                  <div style="margin-top: 15px;">
                    <strong>AI Connection:</strong><br>
                    {{ activeSettings.connection.name }} ({{ activeSettings.connection.modelName }})
                  </div>

                  <div style="margin-top: 15px;">
                    <strong>Assessment Rules:</strong>
                    <pre style="background: #f8f9fa; padding: 10px; border-radius: 4px; max-height: 400px; overflow-y: auto; font-family: 'Courier New', monospace; font-size: 12px;">{{ activeSettings.prompt }}</pre>
                  </div>

                  <div style="margin-top: 15px;">
                    <strong>Chevron Technology Standards:</strong>
                    <pre style="background: #f8f9fa; padding: 10px; border-radius: 4px; max-height: 400px; overflow: auto; white-space: pre-wrap; font-family: 'Courier New', monospace; font-size: 12px;">{{ activeSettings.deprecatedTechnologies || 'Not configured' }}</pre>
                  </div>
                </div>
              </div>
}

              <!-- No Settings Message -->
              @if (!activeSettings && !editingSettings) {
<div class="alert alert-warning">
                <mat-icon>warning</mat-icon>
                No active settings configured. Please create settings to enable AI-powered calculations.
                <br><br>
                <button type="button" class="btn btn-primary no-arrow" (click)="startCreateSettings()">
                  Create Settings
                </button>
              </div>
}

              <!-- Settings Edit Form -->
              @if (editingSettings) {
<div class="card" style="margin-bottom: 20px;">
                <div class="card-body">
                  <h5>{{ isCreatingSettings ? 'Create New Settings Version' : 'Edit Settings Version ' + editingSettings.version }}</h5>

                  <div class="form-group">
                    <label>AI Connection <span class="text-danger">*</span></label>
                    <select class="form-control" [(ngModel)]="editingSettings.aiConnectionId" [ngModelOptions]="{standalone: true}">
                      @for (conn of connections; track conn) {
<option [value]="conn.id">
                        {{ conn.name }} - {{ conn.modelName }} ({{ conn.deployment }})
                      </option>
}
                    </select>
                    <small class="form-text text-muted">
                      <mat-icon style="font-size: 14px; width: 14px; height: 14px; vertical-align: middle;">info</mat-icon>
                      Connections are managed in Admin > AI Config - Connections
                    </small>
                  </div>

                  <div class="form-group">
                    <label>Assessment Rules <span class="text-danger">*</span></label>
                    <textarea class="form-control" [(ngModel)]="editingSettings.prompt" rows="35"
                              placeholder="Define the rules the AI will follow when evaluating each application..."
                              [ngModelOptions]="{standalone: true}"
                              style="font-family: 'Courier New', monospace; font-size: 13px;"></textarea>
                    <small class="form-text text-muted">
                      Define how the AI should evaluate applications — exclusion criteria, scoring sections, outcome logic, and output format. This is saved as a new version each time.
                    </small>
                  </div>

                  <div class="form-group">
                    <label>Chevron Technology Standards (Markdown)</label>
                    <textarea
                      class="form-control"
                      [(ngModel)]="editingSettings.deprecatedTechnologies"
                      rows="18"
                      placeholder="Paste the complete Chevron Technology Standards Markdown document here"
                      [ngModelOptions]="{standalone: true}"
                      style="font-family: 'Courier New', monospace; font-size: 13px;"></textarea>
                    <small class="form-text text-muted">
                      Paste the complete document, including section headings, tables, status and recommendation columns, and footnotes. Lightweight and full code assessments read this saved version automatically; future standards updates do not require prompt changes. Legacy newline-separated deprecated lists remain supported.
                    </small>
                  </div>

                  <div style="display: flex; gap: 10px; margin-top: 20px;">
                    <button type="button" class="btn btn-primary no-arrow" (click)="saveSettings()" [disabled]="saving">
                      @if (saving) {
<mat-spinner diameter="20" style="display: inline-block; margin-right: 5px;"></mat-spinner>
}
                      {{ saving ? 'Saving...' : 'Save Settings' }}
                    </button>
                    <button type="button" class="btn btn-secondary no-arrow" (click)="cancelEditSettings()" [disabled]="saving">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
}

              <!-- Version History -->
              @if (showVersionHistory && !editingSettings) {
<div class="card">
                <div class="card-body">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
                    <h5>Version History</h5>
                    <button type="button" class="btn btn-sm btn-secondary no-arrow" (click)="showVersionHistory = false">
                      Close
                    </button>
                  </div>

                  <table class="table table-striped">
                    <thead>
                      <tr>
                        <th>Version</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Created By</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (version of settingsVersions; track version) {
<tr>
                        <td>{{ version.version }}</td>
                        <td>{{ version.versionName || 'N/A' }}</td>
                        <td>
                          @if (version.isActive) {
<span class="badge badge-success">Active</span>
}
                          @if (!version.isActive) {
<span class="badge badge-secondary">Inactive</span>
}
                        </td>
                        <td>{{ formatDate(version.createdAt) }}</td>
                        <td>{{ version.createdByUserId || 'System' }}</td>
                        <td>
                          <div style="display: flex; gap: 5px;">
                            <button type="button" class="btn btn-sm btn-primary no-arrow" 
                                    (click)="activateVersion(version.id)"
                                    [disabled]="version.isActive">
                              Activate
                            </button>
                            <button type="button" class="btn btn-sm btn-danger no-arrow" 
                                    (click)="deleteVersion(version.id)"
                                    [disabled]="version.isActive">
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
}
                    </tbody>
                  </table>
                </div>
              </div>
}
            </div>
          </mat-tab>

          <!-- Tab 2: Calculate -->
          <mat-tab label="Calculate">
            <div style="padding: 30px;">
              <h3 style="margin-bottom: 10px;">Calculate Modernization Readiness</h3>
              <p class="text-muted" style="margin-bottom: 24px;">
                Calculate modernization readiness scores for applications in your CMDB using AI analysis.
              </p>

              <!-- ============ ACTION PANEL (always visible) ============ -->
              <div class="card" style="margin-bottom: 24px;">
                <div class="card-body">

                  <!-- No settings warning -->
                  @if (!activeSettings) {
<div class="alert alert-warning" style="margin-bottom: 20px;">
                    <mat-icon>warning</mat-icon>
                    Please configure and activate settings in the Settings tab before triggering a calculation.
                  </div>
}

                  <!-- Full calculation row -->
                  <div style="display: flex; align-items: center; gap: 15px; flex-wrap: wrap; margin-bottom: 20px;">
                    <button type="button" class="btn btn-lg btn-primary no-arrow"
                            (click)="triggerCalculation()"
                            [disabled]="triggering || singleAppTriggering || calculationStatus?.isRunning || calculationStatus?.isPaused || !activeSettings">
                      @if (triggering) {
<mat-spinner diameter="20" style="display: inline-block; margin-right: 6px;"></mat-spinner>
}
                      @if (!triggering) {
<mat-icon style="font-size: 18px; width: 18px; height: 18px; vertical-align: middle; margin-right: 4px;">play_arrow</mat-icon>
}
                      {{ triggering ? 'Queuing...' : 'Start Full Calculation' }}
                    </button>
                    <span class="text-muted" style="font-size: 13px;">Calculates readiness scores for all applications in CMDB</span>
                  </div>

                  <hr style="margin: 0 0 20px 0;">

                  <!-- Single app row -->
                  <div>
                    <h6 style="margin-bottom: 6px;">Calculate for a Single App</h6>
                    <p class="text-muted" style="margin-bottom: 12px; font-size: 13px;">Enter an application SysId or name to run the assessment for just one application.</p>
                    <div style="display: flex; gap: 10px; align-items: center; max-width: 640px; flex-wrap: wrap;">
                      <input type="text" class="form-control" [(ngModel)]="singleAppQuery"
                             placeholder="App SysId or Name"
                             [ngModelOptions]="{standalone: true}"
                             [disabled]="singleAppTriggering || triggering || calculationStatus?.isRunning || calculationStatus?.isPaused"
                             (keyup.enter)="triggerCalculationForOne()"
                             style="flex: 1; min-width: 200px;" />
                      <button type="button" class="btn btn-outline-primary no-arrow"
                              (click)="triggerCalculationForOne()"
                              [disabled]="singleAppTriggering || triggering || calculationStatus?.isRunning || calculationStatus?.isPaused || !activeSettings">
                        @if (singleAppTriggering) {
<mat-spinner diameter="18" style="display: inline-block; margin-right: 6px;"></mat-spinner>
}
                        @if (!singleAppTriggering) {
<mat-icon style="font-size: 16px; width: 16px; height: 16px; vertical-align: middle; margin-right: 4px;">search</mat-icon>
}
                        {{ singleAppTriggering ? 'Queuing...' : 'Run for This App' }}
                      </button>
                    </div>
                  </div>

                </div>
              </div>

              <!-- ============ JOB STATUS PANEL (only shown when there is a current job) ============ -->
              @if (currentJob || calculationStatus?.isRunning || calculationStatus?.isPaused) {

                <div class="card">
                  <div class="card-body" style="padding: 25px 30px;">
                    <h5 style="margin-bottom: 20px; color: #495057;">
                      <mat-icon style="vertical-align: middle; margin-right: 6px; font-size: 20px; width: 20px; height: 20px;">monitor</mat-icon>
                      Current Job Status
                    </h5>

                    <!-- Job details (shown as soon as API returns the job) -->
                    @if (currentJob) {
<div>

                      <!-- Status-specific banners -->
                      @if (calculationStatus?.isPaused) {
<div class="alert alert-warning" style="margin-bottom: 20px; padding: 16px 20px;">
                        <div style="display: flex; align-items: center; gap: 12px;">
                          <mat-icon style="font-size: 26px; width: 26px; height: 26px; flex-shrink: 0;">pause_circle_filled</mat-icon>
                          <div>
                            <strong style="font-size: 16px;">Calculation Paused</strong>
                            <p style="margin: 4px 0 0 0; font-size: 13px;">Click Resume to continue processing.</p>
                          </div>
                        </div>
                      </div>
}

                      @if (isStatusCompleted(currentJob.status)) {
<div class="alert alert-success" style="margin-bottom: 20px;">
                        <mat-icon>check_circle</mat-icon>
                        <strong>Calculation completed successfully!</strong>
                        @if (currentJob.totalApplications === 1) {
<span> The application has been processed and scored.</span>
}
                      </div>
}

                      @if (isStatusCancelled(currentJob.status)) {
<div class="alert alert-warning" style="margin-bottom: 20px;">
                        <mat-icon>cancel</mat-icon>
                        Calculation was cancelled.
                      </div>
}

                      @if (isStatusFailed(currentJob.status)) {
<div class="alert alert-danger" style="margin-bottom: 20px;">
                        <mat-icon>error</mat-icon>
                        <strong>Calculation failed:</strong> {{ currentJob.errorMessage }}
                      </div>
}

                      <!-- Job header row: title + controls -->
                      <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 15px; margin-bottom: 20px; flex-wrap: wrap;">
                        <div>
                          <h6 style="margin-bottom: 4px;">
                            Job #{{ currentJob.id }}
                            @if (currentJob.totalApplications === 1) {
<span class="badge badge-info" style="margin-left: 6px; font-size: 11px;">Single App</span>
}
                            &nbsp;—&nbsp;
                            @if (isStatusPending(currentJob.status) && !calculationStatus?.isRunning && !calculationStatus?.isPaused) {
<span class="badge badge-secondary" style="padding: 4px 10px;">Queued</span>
}
                            @if (calculationStatus?.isRunning && !calculationStatus?.isPaused) {
<span class="badge badge-primary" style="padding: 4px 10px;">Running</span>
}
                            @if (calculationStatus?.isPaused) {
<span class="badge badge-warning" style="padding: 4px 10px;">
                              <mat-icon style="font-size: 12px; width: 12px; height: 12px; vertical-align: middle;">pause</mat-icon>
                              Paused
                            </span>
}
                            @if (isStatusCompleted(currentJob.status)) {
<span class="badge badge-success" style="padding: 4px 10px;">Completed</span>
}
                            @if (isStatusCancelled(currentJob.status)) {
<span class="badge badge-warning" style="padding: 4px 10px;">Cancelled</span>
}
                            @if (isStatusFailed(currentJob.status)) {
<span class="badge badge-danger" style="padding: 4px 10px;">Failed</span>
}
                          </h6>
                          <small class="text-muted">
                            Started: {{ formatDate(currentJob.startedAt) }}
                            @if (currentJob.completedAt && (isStatusCompleted(currentJob.status) || isStatusCancelled(currentJob.status))) {
<span>
                              | {{ isStatusCompleted(currentJob.status) ? 'Completed' : 'Cancelled' }}: {{ formatDate(currentJob.completedAt) }}
                            </span>
}
                          </small>
                        </div>

                        <!-- Job controls -->
                        <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
                          <!-- Running controls -->
                          @if (calculationStatus?.isRunning && !calculationStatus?.isPaused) {

                            <button type="button" class="btn btn-sm btn-warning no-arrow" (click)="pauseCalculation()" [disabled]="pausing">
                              <mat-icon style="font-size: 15px; width: 15px; height: 15px; vertical-align: middle;">pause</mat-icon>
                              {{ pausing ? 'Pausing...' : 'Pause' }}
                            </button>
                            <button type="button" class="btn btn-sm btn-danger no-arrow" (click)="cancelCalculation()" [disabled]="cancelling">
                              <mat-icon style="font-size: 15px; width: 15px; height: 15px; vertical-align: middle;">cancel</mat-icon>
                              {{ cancelling ? 'Cancelling...' : 'Cancel' }}
                            </button>
                          
}
                          <!-- Paused controls -->
                          @if (calculationStatus?.isPaused) {

                            <button type="button" class="btn btn-success no-arrow" (click)="resumeCalculation()" [disabled]="resuming">
                              <mat-icon style="font-size: 16px; width: 16px; height: 16px; vertical-align: middle; margin-right: 4px;">play_arrow</mat-icon>
                              {{ resuming ? 'Resuming...' : 'Resume' }}
                            </button>
                            <button type="button" class="btn btn-danger no-arrow" (click)="cancelCalculation()" [disabled]="cancelling">
                              <mat-icon style="font-size: 16px; width: 16px; height: 16px; vertical-align: middle; margin-right: 4px;">cancel</mat-icon>
                              {{ cancelling ? 'Cancelling...' : 'Cancel' }}
                            </button>
                          
}
                          <!-- Dismiss (terminal states) -->
                          @if (isStatusCompleted(currentJob.status) || isStatusCancelled(currentJob.status) || isStatusFailed(currentJob.status)) {
<button
                                  type="button" class="btn btn-sm btn-secondary no-arrow"
                                  (click)="dismissCompletedJob()">
                            <mat-icon style="font-size: 15px; width: 15px; height: 15px; vertical-align: middle; margin-right: 3px;">close</mat-icon>
                            Dismiss
                          </button>
}
                        </div>
                      </div>

                      <!-- Progress bar (running or paused) -->
                      @if (calculationStatus?.isRunning || calculationStatus?.isPaused) {
                        @if (calculationStatus?.currentPhase === 'Scoring') {
<div class="progress" style="height: 30px; margin-bottom: 20px;">
                          <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style="width: 100%">
                            AI Scoring in progress...
                          </div>
                        </div>
                        } @else {
<div class="progress" style="height: 30px; margin-bottom: 20px;">
                          <div class="progress-bar"
                               [ngClass]="{'progress-bar-striped progress-bar-animated': calculationStatus?.isRunning && !calculationStatus?.isPaused, 'bg-warning': calculationStatus?.isPaused}"
                               role="progressbar"
                               [style.width.%]="getProgressPercentage(currentJob)">
                            {{ getProgressPercentage(currentJob) }}%
                          </div>
                        </div>
                        }
}

                      <!-- Statistics: running / paused / failed / cancelled -->
                      @if (!isStatusCompleted(currentJob.status)) {
<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; margin-bottom: 20px;">
                        <div style="padding: 12px 15px; background: #f8f9fa; border-radius: 6px;">
                          <strong style="font-size: 12px; color: #6c757d; text-transform: uppercase;">Progress</strong><br>
                          <span style="font-size: 15px;">{{ currentJob.processedCount }} / {{ currentJob.totalApplications }}</span>
                        </div>
                        <div style="padding: 12px 15px; background: #f8f9fa; border-radius: 6px;">
                          <strong style="font-size: 12px; color: #6c757d; text-transform: uppercase;">Batch</strong><br>
                          <span style="font-size: 15px;">{{ currentJob.currentBatch }} / {{ currentJob.totalBatches }}</span>
                        </div>
                        <div style="padding: 12px 15px; background: #f8f9fa; border-radius: 6px;">
                          <strong style="font-size: 12px; color: #6c757d; text-transform: uppercase;">Success</strong><br>
                          <span class="badge badge-success" style="font-size: 13px;">{{ currentJob.successCount }}</span>
                        </div>
                        <div style="padding: 12px 15px; background: #f8f9fa; border-radius: 6px;">
                          <strong style="font-size: 12px; color: #6c757d; text-transform: uppercase;">Failed</strong><br>
                          <span class="badge badge-danger" style="font-size: 13px;">{{ currentJob.failedCount }}</span>
                        </div>
                        <div style="padding: 12px 15px; background: #f8f9fa; border-radius: 6px;">
                          <strong style="font-size: 12px; color: #6c757d; text-transform: uppercase;">Skipped</strong><br>
                          <span class="badge badge-warning" style="font-size: 13px;">{{ currentJob.skippedCount }}</span>
                        </div>
                        <div style="padding: 12px 15px; background: #f8f9fa; border-radius: 6px;">
                          <strong style="font-size: 12px; color: #6c757d; text-transform: uppercase;">Duration</strong><br>
                          <span style="font-size: 15px;">{{ formatDuration(currentJob) }}</span>
                        </div>
                      </div>
}

                      <!-- Statistics: completed (bigger numbers) -->
                      @if (isStatusCompleted(currentJob.status)) {
<div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 15px; margin-bottom: 20px;">
                        <div class="text-center" style="padding: 20px 15px; background: #f8f9fa; border-radius: 6px;">
                          <h4 class="text-success" style="margin-bottom: 4px;">{{ currentJob.successCount }}</h4>
                          <small class="text-muted">Successful</small>
                        </div>
                        <div class="text-center" style="padding: 20px 15px; background: #f8f9fa; border-radius: 6px;">
                          <h4 class="text-danger" style="margin-bottom: 4px;">{{ currentJob.failedCount }}</h4>
                          <small class="text-muted">Failed</small>
                        </div>
                        <div class="text-center" style="padding: 20px 15px; background: #f8f9fa; border-radius: 6px;">
                          <h4 class="text-warning" style="margin-bottom: 4px;">{{ currentJob.skippedCount }}</h4>
                          <small class="text-muted">Skipped</small>
                        </div>
                        <div class="text-center" style="padding: 20px 15px; background: #f8f9fa; border-radius: 6px;">
                          <h4 style="margin-bottom: 4px;">{{ formatDuration(currentJob) }}</h4>
                          <small class="text-muted">Duration</small>
                        </div>
                      </div>
}

                      <!-- Token usage -->
                      @if ((calculationStatus?.isRunning && calculationStatus) || currentJob.totalTokens > 0) {
<div class="token-usage-card" style="margin-bottom: 20px;">
                        <h6>
                          <mat-icon style="font-size: 18px; width: 18px; height: 18px; vertical-align: middle;">data_usage</mat-icon>
                          Token Usage{{ calculationStatus?.isRunning ? ' (Live)' : isStatusCompleted(currentJob.status) ? ' Summary' : '' }}
                        </h6>
                        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin-top: 15px;">
                          <div class="token-stat">
                            <span class="token-label">Prompt Tokens</span>
                            <span class="token-value">{{ formatTokenCount(calculationStatus?.isRunning && calculationStatus ? calculationStatus.totalPromptTokens : currentJob.totalPromptTokens) }}</span>
                          </div>
                          <div class="token-stat">
                            <span class="token-label">Completion Tokens</span>
                            <span class="token-value">{{ formatTokenCount(calculationStatus?.isRunning && calculationStatus ? calculationStatus.totalCompletionTokens : currentJob.totalCompletionTokens) }}</span>
                          </div>
                          <div class="token-stat">
                            <span class="token-label">Total Tokens</span>
                            <span class="token-value token-total">{{ formatTokenCount(calculationStatus?.isRunning && calculationStatus ? calculationStatus.totalTokens : currentJob.totalTokens) }}</span>
                          </div>
                        </div>
                      </div>
}

                      <!-- Currently processing app (running only) -->
                      @if (calculationStatus?.isRunning) {
<div style="padding: 10px 15px; background: #f8f9fa; border-radius: 6px; margin-bottom: 20px;">
                        <small>
                          @if (calculationStatus?.currentPhase === 'Scoring') {
                            <mat-icon style="font-size: 15px; vertical-align: middle;">smart_toy</mat-icon>
                            AI scoring in progress...
                          } @else if (calculationStatus?.currentPhase === 'Saving' && currentJob.currentApplicationName) {
                            <mat-icon style="font-size: 15px; vertical-align: middle;">save</mat-icon>
                            Saving: <strong>{{ currentJob.currentApplicationName }}</strong>
                          } @else if (currentJob.currentApplicationName) {
                            <mat-icon style="font-size: 15px; vertical-align: middle;">hourglass_empty</mat-icon>
                            Processing: <strong>{{ currentJob.currentApplicationName }}</strong>
                          }
                        </small>
                      </div>
}

                      <!-- Footer: view job history link (terminal states) -->
                      @if (isStatusCompleted(currentJob.status) || isStatusCancelled(currentJob.status) || isStatusFailed(currentJob.status)) {
<div
                           style="padding-top: 15px; border-top: 1px solid #dee2e6;">
                        <button type="button" class="btn btn-secondary no-arrow" (click)="selectedTabIndex = 2">
                          <mat-icon style="font-size: 17px; width: 17px; height: 17px; vertical-align: middle; margin-right: 4px;">history</mat-icon>
                          View Job History
                        </button>
                      </div>
}

                    </div>
}
                  </div>
                </div>
              
}

              <!-- ============ LIVE LOG CONSOLE ============ -->
              <div class="log-console-wrapper">
                <div class="log-console-header">
                  <span>
                    <mat-icon style="font-size: 16px; width: 16px; height: 16px; vertical-align: middle; margin-right: 6px;">terminal</mat-icon>
                    Live Calculation Logs
                  </span>
                  <button type="button" class="btn btn-sm no-arrow log-clear-btn" (click)="clearLogs()" [disabled]="logLines.length === 0">
                    <mat-icon style="font-size: 14px; width: 14px; height: 14px; vertical-align: middle; margin-right: 3px;">clear_all</mat-icon>
                    Clear
                  </button>
                </div>
                <div class="log-console-body" #logConsoleEl>
                  <div *ngIf="logLines.length === 0" class="log-line log-empty">Waiting for calculation to start&hellip;</div>
                  <div *ngFor="let line of logLines" class="log-line"
                       [class.log-warn]="line.includes('[WARN]')"
                       [class.log-error]="line.includes('[ERRO') || line.includes('[CRIT')">{{ line }}</div>
                </div>
              </div>

            </div>
          </mat-tab>

          <!-- Tab 3: Job History -->
          <mat-tab label="Job History">
            <div style="padding: 20px;">
              <h3>Calculation History</h3>
              <p class="text-muted">View past calculation jobs and their results</p>

              @if (jobHistoryLoading) {
<app-modernization-readiness-job-history-skeleton></app-modernization-readiness-job-history-skeleton>
}

              @if (!jobHistoryLoading && jobHistory.length === 0) {
<div class="alert alert-info">
                <mat-icon>info</mat-icon>
                No calculation jobs found. Trigger a calculation to see history.
              </div>
}

              @if (!jobHistoryLoading && jobHistory.length > 0) {
<div class="card">
                <div class="card-body">
                  <table class="table table-striped">
                    <thead>
                      <tr>
                        <th>Job ID</th>
                        <th>Started</th>
                        <th>Status</th>
                        <th>Progress</th>
                        <th>Duration</th>
                        <th>Results</th>
                        <th>Tokens</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (job of jobHistory; track job) {
<tr>
                        <td>{{ job.id }}</td>
                        <td>{{ formatDate(job.createdAt) }}</td>
                        <td>
                          <span [class]="'badge ' + getStatusBadgeClass(job.status)">
                            {{ getStatusText(job.status) }}
                          </span>
                        </td>
                        <td>{{ job.processedCount }} / {{ job.totalApplications }}</td>
                        <td>{{ formatDuration(job) }}</td>
                        <td>
                          <small>
                            <span class="badge badge-success">{{ job.successCount }}</span>
                            <span class="badge badge-danger">{{ job.failedCount }}</span>
                            <span class="badge badge-warning">{{ job.skippedCount }}</span>
                          </small>
                        </td>
                        <td>
                          @if (job.totalTokens && job.totalTokens > 0) {
<div style="font-size: 11px; line-height: 1.4;">
                            <div title="Prompt tokens">
                              <span class="badge badge-secondary"><strong>P:</strong>&nbsp;{{ formatTokenCount(job.totalPromptTokens) }}</span>
                            </div>
                            <div title="Completion tokens" style="margin-top: 2px; padding-top: 2px;">
                              <span class="badge badge-secondary"><strong>C:</strong>&nbsp;{{ formatTokenCount(job.totalCompletionTokens) }}</span>
                            </div>
                            <div title="Total tokens" style="margin-top: 2px; padding-top: 2px;">
                               <span class="badge badge-success"><strong>T:</strong>&nbsp;{{ formatTokenCount(job.totalTokens) }}</span>
                            </div>
                          </div>
}
                          @if (!job.totalTokens || job.totalTokens === 0) {
<small>-</small>
}
                        </td>
                        <td>
                          <button type="button" class="btn btn-sm btn-info no-arrow" (click)="viewJobDetails(job)">
                            View Logs
                          </button>
                        </td>
                      </tr>
}
                    </tbody>
                  </table>

                  <mat-paginator #jobHistoryPaginator
                    [length]="jobHistoryTotalCount"
                    [pageSize]="jobHistoryPageSize"
                    [pageIndex]="jobHistoryPage - 1"
                    [pageSizeOptions]="[10, 20, 50, 100]"
                    [showFirstLastButtons]="true"
                    (page)="onJobHistoryPageChange($event)">
                  </mat-paginator>
                </div>
              </div>
}
            </div>
          </mat-tab>

        </mat-tab-group>
}
